import React, { useState, useLayoutEffect } from 'react'
import { graphql } from 'gatsby'
import cn from 'classnames'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { connect } from 'react-redux'
import { useInView } from 'react-intersection-observer'

import {
    Icon,
    SubheroImage,
    Modal,
    Usp,
} from '@gymondo/frontend-core/components'
import { Program as ProgramGeneric } from '@gymondo/frontend-core/page-sections'
import { useMediaQuery } from '@gymondo/frontend-core/hooks'
import { mediaXsOnly } from '@gymondo/frontend-core/style-vars'
import { FormattedMessage, useIntl } from 'react-intl'
import qs from 'query-string'
import Workout from './component/workout/workout'
import { BackgroundImage, Cta, LinkLocalized } from '../../../component'
import { setConfig } from '../../../service/layout'
import reviews from '../../../service/review/review.constant'

import style from './program.module.scss'

// Generate slug from title
// Same logic used by the gymondo-content-library-parsing scripts
function makeSlug(title) {
    return title
        .replace(/ß/g, 'ss')
        .replace(/\\/g, '')
        .replace(/-/g, '')
        .replace(/\//g, '')
        .replace(/&/g, 'and')
        .replace(/ä/g, 'ae')
        .replace(/ü/g, 'ue')
        .replace(/ö/g, 'oe')
        .replace(/,/g, '')
        .replace(/:/g, '')
        .replace(/ /g, '')
        .toLowerCase()
}

const NEW_CDN_URI = 'https://cdn1.service.prod.gymondo.io'

const HIDE_HERO_ICON_BAR_PROGRAM_IDS = [4081, 941]

const renderHeader = (isSmall, data, setShowModal) => {
    const location = typeof window !== 'undefined' && window?.location
    const queryParams = qs.parse(location && location.search)
    const { male } = queryParams
    const assetsPostfix = male === 'true' ? 'Male' : ''
    const heroImagePath = `details.imagesNode.header${assetsPostfix}.childImageSharp.fluid`
    const heroImageMobilePath = `details.imagesNode.headerMobile${assetsPostfix}.childImageSharp.fluid`
    const heroImage = _.get(data, heroImagePath)
    const heroMobileImage = _.get(data, heroImageMobilePath)
    const {
        imageUrl,
        title,
        goal,
        level,
        durationWeeks,
        videoUrl,
        idParent,
    } = data

    const CTA = () => (
        <Cta displayXS fixedXS={false} inversed btnSize="medium" />
    )

    const BgImg = () => (
        <BackgroundImage
            fluid={isSmall ? heroMobileImage : heroImage}
            imgClassName="image-position__50"
            gradient
            overlay
        />
    )

    const setShowModalCallback = () => setShowModal(`${videoUrl}_360.mp4`)

    return (
        <ProgramGeneric.Header
            BackgroundImage={BgImg}
            cta={CTA}
            program={{
                image: `${imageUrl}webapp-titletext.svg`,
                id: idParent,
                title,
                goal,
                level,
                durationWeeks,
            }}
            setShowModal={videoUrl && setShowModalCallback}
            messages={{
                preview: 'program.header.preview',
                level: 'program.header.level',
                durationWeek: 'program.header.durationWeek',
            }}
        />
    )
}

const renderUSPs = (data) => {
    const {
        usp,
        goal: { color },
    } = data
    const uspsImages = _.map(_.get(data, 'details.imagesNode.usps'), (img) =>
        _.get(img, 'childImageSharp.fluid')
    )

    const renderImage = (image) => <BackgroundImage fluid={image} />

    const uspItems = usp.map((it, index) => ({
        ...it,
        image: uspsImages[index],
    }))

    return (
        <div className={cn(style.uspWrapper)}>
            <Usp
                renderImage={renderImage}
                usps={uspItems}
                color={`#${color}`}
            />
        </div>
    )
}

const renderPreviews = (data, setShowModal, intl) => {
    const previews = _.map(_.get(data, 'details.imagesNode.previews'), (img) =>
        _.get(img, 'childImageSharp.fluid')
    )
    const {
        title,
        description,
        tools,
        idParent,
        cardio,
        coordination,
        mobility,
        strength,
    } = data

    const pieChart = [
        {
            label: 'piechart.category.cardio',
            value: cardio,
            color: '#d99984',
        },
        {
            label: 'piechart.category.strength',
            value: strength,
            color: '#677cc7',
        },
        {
            label: 'piechart.category.coordination',
            value: coordination,
            color: '#f07580',
        },
        {
            label: 'piechart.category.mobility',
            value: mobility,
            color: '#4fb8ad',
        },
    ]

    const renderImage = (playSvg, preview) => (
        <BackgroundImage fluid={preview}>
            <Icon data={playSvg} />
        </BackgroundImage>
    )

    return (
        <ProgramGeneric.Preview
            renderImage={renderImage}
            program={{
                id: idParent,
                title,
                description,
                previews,
                // TODO: Remove this when handling of the preview videos is done by CMS
                image: `${NEW_CDN_URI}/program-video-preview/${idParent}-${makeSlug(
                    title
                )}/${intl.locale.substring(0, 2)}/`,
                tools,
            }}
            setShowModal={setShowModal}
            chartData={pieChart}
            messages={{
                workout: 'program.preview.workout',
                why: 'program.preview.why',
                division: 'program.preview.division',
                tools: 'program.preview.tools',
                none: 'program.preview.tool.none',
            }}
        />
    )
}

/**
 * Open a modal with the video inside an iframe
 * @param {String} videoUrl
 * @param {function} setshowModal Toogle modal
 */
const renderModal = (videoUrl, setShowModal) => (
    <Modal
        onClose={() => setShowModal(null)}
        dialogClassName={style.modalDialog}
        hideFooter
    >
        <video
            className={style.previewVideo}
            controls
            autoPlay
            name="media"
            webkitallowfullscreen="true"
            mozallowfullscreen="true"
            allowFullScreen
        >
            <source src={videoUrl} type="video/mp4" />
            <track />
        </video>
    </Modal>
)

const renderWorkouts = (data) => {
    const workoutsImages = _.map(
        _.get(data, 'details.imagesNode.workouts'),
        (img) => {
            const image = _.get(img, 'childImageSharp.fluid')

            return (
                image && (
                    <BackgroundImage
                        fluid={image}
                        imgClassName={style.imgClassName}
                    />
                )
            )
        }
    )
    const {
        idParent,
        details: { workouts },
        goal: { color },
    } = data

    return (
        <Workout
            programId={idParent}
            workoutImages={workoutsImages}
            workouts={workouts}
            color={`#${color}`}
        />
    )
}

const renderBottom = (dataBottom) => {
    const imgBottom = _.get(
        dataBottom,
        'bottom.edges.0.node.childImageSharp.fluid'
    )

    const BgImg = () => (
        <BackgroundImage
            fluid={imgBottom}
            overlay
            classParentName="image-position__65"
        />
    )

    return (
        <SubheroImage
            BackgroundImage={BgImg}
            messages={{
                title: 'program.bottom.0.title',
                subtitle: 'program.bottom.0.subtitle',
            }}
        >
            <Cta displayXS fixedXS />
        </SubheroImage>
    )
}

const renderReview = () => (
    <ProgramGeneric.Review
        messages={{
            title: 'program.review.title',
        }}
        reviews={reviews}
    />
)

const renderSimilarPrograms = (data, ref, inView) => {
    const {
        goal,
        details: { similarPrograms },
    } = data

    const Link = (program, children) => (
        <LinkLocalized to="programs" view={program.slug} alt={program.title}>
            {children}
        </LinkLocalized>
    )

    return (
        !_.isEmpty(similarPrograms) && (
            <div ref={ref}>
                {inView && (
                    <ProgramGeneric.SimilarPrograms
                        linkComponent={Link}
                        goal={goal}
                        similarPrograms={similarPrograms}
                        messages={{
                            title: 'program.similar.title',
                        }}
                    />
                )}
            </div>
        )
    )
}

export const Program = ({ data, pageContext, setLayoutConfig }) => {
    const intl = useIntl()
    const dataNode = _.get(data, 'allProgram.edges.0.node')

    const [modalUrl, setShowModal] = useState(null)
    const isSmall = useMediaQuery(mediaXsOnly)

    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '100px',
    })

    useLayoutEffect(() => {
        setLayoutConfig({
            textColorVariant: 'white',
        })
    })

    if (pageContext?.redirectURL) {
        return (
            <div className={cn('content-wrapper', style.redirectionPage)}>
                <FormattedMessage
                    id="program.redirect"
                    values={{
                        program: () => <strong>{dataNode.title}</strong>,
                        link: (msg) => (
                            <a href={pageContext.redirectURL}>{msg}</a>
                        ),
                    }}
                />
            </div>
        )
    }

    const shouldHideHeroIconBar = HIDE_HERO_ICON_BAR_PROGRAM_IDS.includes(
        dataNode.idParent
    )

    return (
        <div
            className={cn(style.page, {
                [style.hideHeroIconBar]: shouldHideHeroIconBar,
            })}
        >
            {renderHeader(isSmall, dataNode, setShowModal)}
            {renderUSPs(dataNode)}
            {renderPreviews(dataNode, setShowModal, intl)}
            {renderWorkouts(dataNode)}
            {renderBottom(data, dataNode)}
            {renderReview()}
            {renderSimilarPrograms(dataNode, ref, inView)}
            {modalUrl && renderModal(modalUrl, setShowModal)}
        </div>
    )
}

Program.propTypes = {
    data: PropTypes.object.isRequired,
    pageContext: PropTypes.object.isRequired,
    setLayoutConfig: PropTypes.func.isRequired,
}

export default connect(null, {
    setLayoutConfig: setConfig,
})(Program)

export const query = graphql`
    query($id: String!, $goalRegex: String!) {
        bottom: allFile(filter: { absolutePath: { regex: $goalRegex } }) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 3840
                            srcSetBreakpoints: [1400, 1900, 2200, 3840]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        allProgram(filter: { id: { eq: $id } }) {
            edges {
                node {
                    id
                    idParent
                    description
                    title
                    imageUrl
                    videoUrl
                    cardio
                    coordination
                    mobility
                    strength
                    tools {
                        id
                        name
                    }
                    goal {
                        id
                        name
                        color
                    }
                    level
                    durationWeeks
                    usp {
                        headline
                        body
                    }
                    details {
                        similarPrograms {
                            imageUrl
                            slug
                            title
                        }
                        workouts {
                            totalElements
                            content {
                                title
                                categoryTitle
                                durationSeconds
                                goal {
                                    color
                                }
                            }
                        }
                        imagesNode {
                            header {
                                childImageSharp {
                                    ...ImageSharpFragment
                                }
                            }
                            headerMale {
                                childImageSharp {
                                    ...ImageSharpFragment
                                }
                            }
                            headerMobile {
                                childImageSharp {
                                    fluid(
                                        maxWidth: 1400
                                        srcSetBreakpoints: [1400, 1000]
                                        toFormat: NO_CHANGE
                                        quality: 90
                                    ) {
                                        ...GatsbyImageSharpFluid_withWebp
                                    }
                                }
                            }
                            headerMobileMale {
                                childImageSharp {
                                    fluid(
                                        maxWidth: 1400
                                        srcSetBreakpoints: [1400, 1000]
                                        toFormat: NO_CHANGE
                                        quality: 90
                                    ) {
                                        ...GatsbyImageSharpFluid_withWebp
                                    }
                                }
                            }
                            usps {
                                childImageSharp {
                                    fluid(
                                        maxWidth: 3840
                                        srcSetBreakpoints: [
                                            1400
                                            1900
                                            2200
                                            3840
                                        ]
                                        toFormat: NO_CHANGE
                                        quality: 90
                                    ) {
                                        ...GatsbyImageSharpFluid_withWebp
                                    }
                                }
                            }
                            previews {
                                childImageSharp {
                                    fluid(
                                        maxWidth: 3840
                                        srcSetBreakpoints: [
                                            1400
                                            1900
                                            2200
                                            3840
                                        ]
                                        toFormat: NO_CHANGE
                                        quality: 90
                                    ) {
                                        ...GatsbyImageSharpFluid_withWebp
                                    }
                                }
                            }
                            workouts {
                                childImageSharp {
                                    fluid(
                                        maxWidth: 3840
                                        srcSetBreakpoints: [
                                            1400
                                            1900
                                            2200
                                            3840
                                        ]
                                        toFormat: NO_CHANGE
                                        quality: 90
                                    ) {
                                        ...GatsbyImageSharpFluid_withWebp
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    fragment ImageSharpFragment on ImageSharp {
        fluid(
            maxWidth: 3840
            srcSetBreakpoints: [1400, 1900, 2200, 3840]
            toFormat: NO_CHANGE
            quality: 90
        ) {
            ...GatsbyImageSharpFluid_withWebp
        }
    }
`
